(() => {
  if (window.customElements.get('product-tabs')) return;

  /*
    The theme's CustomDetails class observes [open] and [aria-expanded] but
    doesn't define attributeChangedCallback, so setting details.open directly
    leaves aria-expanded stale. We sync both whenever we change open state.
  */
  function setOpenState(details, shouldBeOpen) {
    if (details.open !== shouldBeOpen) details.open = shouldBeOpen;
    details.setAttribute('aria-expanded', shouldBeOpen ? 'true' : 'false');
  }

  class ProductTabs extends HTMLElement {
    constructor() {
      super();
      this._onNavClick = this._onNavClick.bind(this);
      this._onNavKeydown = this._onNavKeydown.bind(this);
    }

    connectedCallback() {
      this.nav = this.querySelector('.product-tabs__nav');
      this.tabs = Array.from(this.querySelectorAll('.product-tabs__tab'));
      this.panels = Array.from(this.querySelectorAll('.product-tabs__panel'));
      if (!this.nav || this.tabs.length === 0 || this.panels.length === 0) return;

      this._activeIndex = 0;

      this.nav.addEventListener('click', this._onNavClick);
      this.nav.addEventListener('keydown', this._onNavKeydown);

      this.panels.forEach((panel) => {
        const details = panel.querySelector('details');
        if (details) setOpenState(details, true);
      });

      this._renderTabState();
      this.setAttribute('data-tabs-active', '');
    }

    disconnectedCallback() {
      if (this.nav) {
        this.nav.removeEventListener('click', this._onNavClick);
        this.nav.removeEventListener('keydown', this._onNavKeydown);
      }
    }

    _renderTabState() {
      this.tabs.forEach((tab, i) => {
        const isActive = i === this._activeIndex;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panels[i].id);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel, i) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', this.tabs[i].id);
        if (i === this._activeIndex) {
          panel.setAttribute('data-active', '');
        } else {
          panel.removeAttribute('data-active');
        }
      });
    }

    _activate(index, { userInitiated = false } = {}) {
      if (index < 0 || index >= this.tabs.length) return;
      this._activeIndex = index;
      this._renderTabState();
      if (userInitiated) {
        this.tabs[index].focus();
        this.tabs[index].scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
      }
    }

    _onNavClick(event) {
      const tab = event.target.closest('.product-tabs__tab');
      if (!tab) return;
      const index = this.tabs.indexOf(tab);
      if (index === -1) return;
      this._activate(index, { userInitiated: true });
    }

    _onNavKeydown(event) {
      const currentIndex = this.tabs.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      let nextIndex = null;
      switch (event.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % this.tabs.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = this.tabs.length - 1;
          break;
        default:
          return;
      }
      event.preventDefault();
      this._activate(nextIndex, { userInitiated: true });
    }
  }

  window.customElements.define('product-tabs', ProductTabs);
})();
